import { isJSON, successResponse } from 'core';
import { getLessonItems } from 'core/crud/lesson.crud';
import { getTestQuestions } from 'core/crud/tests.crud';
import { useCourse } from 'core/lite/hooks/useCourse';
import { useUser } from 'core/lite/hooks/useUser';
import { getCustomizedBy, getGuidCourseByTemplate } from 'core/utils/course';
import { parseMaybeJson } from 'core/utils/json.utils';
import { useCallback, useState } from 'react';
import { regenerateQuizRevisionConversation } from '../services/quizRevisionService';

/**
 * Parse URL to extract revision page route params
 * Expected URL format: /results-assesment-individual/course/:courseGuid/:assessmentGuid/user/:userGuid/question/:questionGuid
 * @returns {Object|null} Route params or null if not on revision page
 */
function parseRevisionUrlParams() {
  const pathname = window.location.pathname;

  // Match the revision question page URL pattern
  const match = pathname.match(/\/results-assesment-individual\/course\/([^/]+)\/([^/]+)\/user\/([^/]+)\/question\/([^/]+)/);

  if (!match) {
    return null;
  }

  return {
    courseGuid: match[1],
    assessmentGuid: match[2],
    userGuid: match[3],
    questionGuid: match[4],
  };
}

/**
 * Build learning objectives map from raw questions
 * @param {Array} rawQuestions - Array of question objects
 * @returns {Object} Map of questionGuid to learning objectives
 */
function buildLearningObjectivesMap(rawQuestions) {
  const learningObjectivesMap = {};
  if (rawQuestions && Array.isArray(rawQuestions)) {
    rawQuestions.forEach((question) => {
      try {
        const questionGuid = question?.guid || question?.question_guid;
        if (questionGuid && question.learningObjectives) {
          learningObjectivesMap[questionGuid] = question.learningObjectives;
        }
      } catch (err) {
        console.warn('[useRevisionRegenerate] Error parsing question data for learning objectives:', err);
      }
    });
  }
  return learningObjectivesMap;
}

/**
 * Map learning objectives to the expected API format
 * @param {Array} learningObjectives - Array of learning objectives
 * @returns {Object} Mapped learning objectives
 */
function mapLearningObjectives(learningObjectives) {
  if (!learningObjectives || learningObjectives.length === 0) {
    return {
      cognitivePhase: '',
      microhability: '',
      nanohability: '',
    };
  }

  const parentLearningObjective = learningObjectives[0]?.parent?.learning_objective;
  const parentLearningObjectiveArray = parentLearningObjective?.split(' - ') || [];
  const cognitivePhase = parentLearningObjectiveArray[0] || '';
  const microhability = parentLearningObjectiveArray[1] || '';
  const nanohability = learningObjectives[0]?.learning_objective || '';

  return {
    cognitivePhase,
    microhability,
    nanohability,
  };
}

/**
 * Extract question id for quiz revision (questionGuid)
 * @param {Object} question - Question object with data field
 * @param {Object} answer - Answer object with question field
 * @returns {string|null} Question guid
 */
function getQuestionReferenceId(question, answer) {
  try {
    return question?.guid || question?.question_guid || answer?.questionGuid || null;
  } catch {
    return null;
  }
}

/**
 * Hook to regenerate a quiz revision conversation
 * Fetches all required data on-the-fly when regenerate is called
 * @returns {Object} { regenerate, isRegenerating, error }
 */
export function useRevisionRegenerate() {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useUser();

  // Parse URL params to get courseGuid for useCourse hook
  const urlParams = parseRevisionUrlParams();
  const { data: course } = useCourse(urlParams?.courseGuid);

  const regenerate = useCallback(async () => {
    setIsRegenerating(true);
    setError(null);

    try {
      // Parse URL to get route params
      const params = parseRevisionUrlParams();
      if (!params) {
        throw new Error('Not on a revision question page');
      }

      const { assessmentGuid, userGuid, questionGuid } = params;

      if (!course?.guid) {
        throw new Error('Course data not available');
      }

      if (!user?.guid) {
        throw new Error('User not authenticated');
      }

      // Fetch lesson items to get job with metadata
      const lessonItemResponse = await getLessonItems(assessmentGuid, getGuidCourseByTemplate(course), getCustomizedBy(course, { guid: userGuid }));

      if (!successResponse(lessonItemResponse)) {
        throw new Error('Failed to fetch lesson items');
      }

      const job = lessonItemResponse?.data?.data?.job;
      if (!job?.guid || !job?.metadata) {
        throw new Error('Job data not found');
      }

      // Parse job metadata to get answers
      let quizQuestionData;
      try {
        quizQuestionData = JSON.parse(job.metadata);
      } catch {
        throw new Error('Failed to parse job metadata');
      }

      const answers = quizQuestionData?.answers || [];

      // Parse content_data to get questions
      const content = lessonItemResponse?.data?.data?.content_data;
      let questions = [];
      if (content && isJSON(content)) {
        const parsedContent = JSON.parse(content);
        questions = parsedContent.questions || [];
      }

      // Find the question index by matching question_guid from URL
      console.log('questions', questions);
      console.log('questionGuid', questionGuid);
      const questionIndex = questions.findIndex((q) => q.guid === questionGuid);
      if (questionIndex === -1) {
        throw new Error('Question not found in assessment');
      }

      // Get the question and corresponding answer at that index
      const question = questions[questionIndex];
      const answer = answers[questionIndex];

      // The questionReferenceId is the questionGuid (no need to read "reference" from inside question JSON)
      const questionReferenceId = questionGuid || getQuestionReferenceId(question, answer);

      if (!questionReferenceId) {
        throw new Error('Could not find question reference ID');
      }

      // Fetch test questions to get learning objectives
      let learningObjectivesMap = {};
      const contentGuid = lessonItemResponse?.data?.data?.content_guid;
      if (contentGuid) {
        try {
          const questionsResponse = await getTestQuestions({ guid: contentGuid });
          if (successResponse(questionsResponse)) {
            const rawQuestions = questionsResponse?.data?.data?.data?.questions;
            learningObjectivesMap = buildLearningObjectivesMap(rawQuestions);
          }
        } catch (e) {
          console.warn('[useRevisionRegenerate] Could not fetch learning objectives:', e);
          // Continue without learning objectives
        }
      }

      // Map learning objectives for this question (keyed by questionGuid)
      const questionLearningObjectives = learningObjectivesMap[questionReferenceId] || [];
      const mappedLearningObjectives = mapLearningObjectives(questionLearningObjectives);

      // Build the regenerate payload
      const payload = {
        externalUserId: user.guid,
        quizJobGuid: job.guid,
        questionReferenceId,
        quizQuestionData: { ...answer, question: parseMaybeJson(answer.question), answer: parseMaybeJson(answer.answer) },
        learningObjectives: mappedLearningObjectives,
      };

      // Call regenerate API
      const response = await regenerateQuizRevisionConversation(payload);

      if (response?.conversationId) {
        // Success - reload the page to load the new conversation
        window.location.reload();
      } else {
        throw new Error('Regenerate API did not return a conversation ID');
      }
    } catch (err) {
      console.error('[useRevisionRegenerate] Error:', err);
      setError(err.message || 'Failed to regenerate conversation');
    } finally {
      setIsRegenerating(false);
    }
  }, [course, user]);

  return {
    regenerate,
    isRegenerating,
    error,
    isAvailable: !!urlParams && !!course?.guid && !!user?.guid,
  };
}

export default useRevisionRegenerate;
